/**
 * Decode the packed binary surface produced by
 * ``backend/mesh/surface_extractor.py``.
 *
 * Layout: MAGIC | uint32 header length | JSON header | binary blocks.
 * The header records a byte offset and length for every block, so each array
 * below is a *view* over the response buffer -- no copying, no parsing of
 * numbers, and nothing routed through JSON.
 */

const MAGIC = 'FLWMESH2';
const HEADER_LENGTH_OFFSET = MAGIC.length;
const BLOCKS_OFFSET = MAGIC.length + 4;

export class SurfaceDecodeError extends Error {
  constructor(message) {
    super(message);
    this.name = 'SurfaceDecodeError';
  }
}

/** Read the leading magic bytes without decoding the whole buffer. */
function readMagic(buffer) {
  const bytes = new Uint8Array(buffer, 0, Math.min(MAGIC.length, buffer.byteLength));
  return new TextDecoder('ascii').decode(bytes);
}

/**
 * Build a typed-array view over one block, verifying it lies inside the
 * buffer. A truncated response would otherwise surface much later as a
 * confusing WebGL error.
 */
function viewBlock(buffer, base, block, TypedArray, label) {
  if (!block || typeof block.offset !== 'number' || typeof block.byteLength !== 'number') {
    throw new SurfaceDecodeError(`Surface payload is missing its "${label}" block`);
  }

  const start = base + block.offset;
  const end = start + block.byteLength;
  if (end > buffer.byteLength) {
    throw new SurfaceDecodeError(
      `Surface payload is truncated: "${label}" needs ${end} bytes, got ${buffer.byteLength}`,
    );
  }
  if (block.byteLength % TypedArray.BYTES_PER_ELEMENT !== 0) {
    throw new SurfaceDecodeError(`Block "${label}" is not a whole number of elements`);
  }

  return new TypedArray(buffer, start, block.byteLength / TypedArray.BYTES_PER_ELEMENT);
}

/**
 * Decode a surface payload.
 *
 * @param {ArrayBuffer} buffer raw response body
 * @returns {{
 *   source: string,
 *   numberOfPoints: number,
 *   numberOfPolys: number,
 *   bounds: number[],
 *   points: Float32Array,
 *   offsets: Int32Array,
 *   connectivity: Int32Array,
 *   arrays: Array<{name: string, location: string, components: number,
 *                  range: number[], values: Float32Array}>,
 * }}
 */
export function decodeSurface(buffer) {
  if (!(buffer instanceof ArrayBuffer)) {
    throw new SurfaceDecodeError('Expected an ArrayBuffer');
  }
  if (buffer.byteLength < BLOCKS_OFFSET) {
    throw new SurfaceDecodeError('Surface payload is too short to contain a header');
  }
  if (readMagic(buffer) !== MAGIC) {
    // Almost always an HTML error page served with the wrong content type.
    throw new SurfaceDecodeError('Response is not a FlowLab surface payload');
  }

  const headerLength = new DataView(buffer).getUint32(HEADER_LENGTH_OFFSET, true);
  const headerEnd = BLOCKS_OFFSET + headerLength;
  if (headerEnd > buffer.byteLength) {
    throw new SurfaceDecodeError('Surface payload header extends past the end of the response');
  }

  let header;
  try {
    header = JSON.parse(new TextDecoder('utf-8').decode(new Uint8Array(buffer, BLOCKS_OFFSET, headerLength)));
  } catch (cause) {
    throw new SurfaceDecodeError(`Surface payload header is not valid JSON: ${cause.message}`);
  }

  const points = viewBlock(buffer, headerEnd, header.points, Float32Array, 'points');
  const offsets = viewBlock(buffer, headerEnd, header.offsets, Int32Array, 'offsets');
  const connectivity = viewBlock(buffer, headerEnd, header.connectivity, Int32Array, 'connectivity');

  const arrays = (header.arrays || []).map((array) => ({
    name: array.name,
    location: array.location,
    components: array.components,
    range: array.range,
    values: viewBlock(buffer, headerEnd, array, Float32Array, array.name),
  }));

  return {
    source: header.source || '',
    numberOfPoints: header.numberOfPoints || 0,
    numberOfPolys: header.numberOfPolys || 0,
    bounds: header.bounds || [0, 0, 0, 0, 0, 0],
    points,
    offsets,
    connectivity,
    arrays,
  };
}
